// src/pages/AuditLog.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ExportButton from '../components/ExportButton';

function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    admin_id: '',
    start_date: '',
    end_date: ''
  });
  const [actions, setActions] = useState([]);

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  useEffect(() => {
    fetchAuditLogs();
    fetchFilterOptions();
  }, [filters, offset, limit]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        limit,
        offset,
        ...filters
      });
      
      const response = await axios.get(
        `${API_URL}/audit-logs?${params}`,
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/audit-logs/summary`,
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      
      const uniqueActions = [...new Set(response.data.summary?.map(s => s.action) || [])];
      setActions(uniqueActions);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setOffset(0);
  };

  const handleClearFilters = () => {
    setFilters({
      action: '',
      entity_type: '',
      admin_id: '',
      start_date: '',
      end_date: ''
    });
    setOffset(0);
  };

  const getActionColor = (action) => {
    const colors = {
      'approve': '#10b981',
      'block': '#ef4444',
      'suspend': '#f59e0b',
      'terminate': '#dc2626',
      'unblock': '#10b981',
      'restore': '#3b82f6',
      'reject': '#ef4444',
      'create': '#8b5cf6',
      'update': '#3b82f6',
      'delete': '#ef4444',
      'login': '#10b981',
      'logout': '#ef4444'
    };
    return colors[action] || '#64748b';
  };

  const getActionEmoji = (action) => {
    const emojis = {
      'approve': '✅',
      'block': '🚫',
      'suspend': '⏸️',
      'terminate': '⚠️',
      'unblock': '🔄',
      'restore': '↩️',
      'reject': '❌',
      'create': '➕',
      'update': '✏️',
      'delete': '🗑️',
      'login': '🔑',
      'logout': '🚪'
    };
    return emojis[action] || '📝';
  };

  const getExportData = () => {
    return logs.map(log => ({
      'Admin': log.admin_name,
      'Action': log.action,
      'Entity Type': log.entity_type,
      'Entity ID': log.entity_id,
      'Details': JSON.stringify(log.details || {}),
      'IP': log.ip_address,
      'Date': new Date(log.created_at).toLocaleString(),
    }));
  };

  if (loading && logs.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading audit logs...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Audit Log</h1>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '14px', color: '#64748b' }}>
            <span>📋 {total} entries</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <ExportButton 
            data={getExportData()} 
            filename="audit_log_export" 
            label="Export CSV"
          />
          <button
            onClick={handleClearFilters}
            style={{
              padding: '8px 16px',
              backgroundColor: '#e2e8f0',
              color: '#475569',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Clear Filters
          </button>
          <button
            onClick={fetchAuditLogs}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        <div>
          <label style={{ fontSize: '12px', color: '#64748b', display: 'block' }}>Action</label>
          <select
            value={filters.action}
            onChange={(e) => handleFilterChange('action', e.target.value)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              minWidth: '120px',
            }}
          >
            <option value="">All Actions</option>
            {actions.map(action => (
              <option key={action} value={action}>{action}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: '12px', color: '#64748b', display: 'block' }}>Entity Type</label>
          <select
            value={filters.entity_type}
            onChange={(e) => handleFilterChange('entity_type', e.target.value)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              minWidth: '120px',
            }}
          >
            <option value="">All Types</option>
            <option value="driver">Driver</option>
            <option value="customer">Customer</option>
            <option value="ride">Ride</option>
            <option value="voucher">Voucher</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>

      {/* Audit Log Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'auto',
      }}>
        {logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
            <p style={{ color: '#64748b', fontSize: '16px' }}>No audit logs found</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '12px' }}>Date & Time</th>
                <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '12px' }}>Admin</th>
                <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '12px' }}>Action</th>
                <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '12px' }}>Entity</th>
                <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '12px' }}>Details</th>
                <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '12px' }}>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px', fontSize: '12px', color: '#64748b' }}>
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px', fontWeight: '500' }}>
                    {log.admin_name || 'Unknown'}
                  </td>
                  <td style={{ padding: '10px' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      backgroundColor: getActionColor(log.action) + '20',
                      color: getActionColor(log.action),
                    }}>
                      {getActionEmoji(log.action)} {log.action}
                    </span>
                  </td>
                  <td style={{ padding: '10px', fontSize: '13px' }}>
                    {log.entity_type} #{log.entity_id}
                  </td>
                  <td style={{ padding: '10px', fontSize: '12px', color: '#64748b' }}>
                    {log.details ? JSON.stringify(log.details).substring(0, 50) : '—'}
                  </td>
                  <td style={{ padding: '10px', fontSize: '12px', color: '#94a3b8' }}>
                    {log.ip_address || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        
        {total > limit && (
          <div style={{ 
            padding: '12px', 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid #e2e8f0',
            marginTop: '12px',
          }}>
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              Showing {offset + 1} to {Math.min(offset + limit, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                style={{
                  padding: '6px 12px',
                  backgroundColor: offset === 0 ? '#e2e8f0' : '#f97316',
                  color: offset === 0 ? '#94a3b8' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: offset === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                Previous
              </button>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
                style={{
                  padding: '6px 12px',
                  backgroundColor: offset + limit >= total ? '#e2e8f0' : '#f97316',
                  color: offset + limit >= total ? '#94a3b8' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: offset + limit >= total ? 'not-allowed' : 'pointer',
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuditLog;